import {
  TEMPLATE,
  TEMPLATE_CATEGORY,
} from "../approved-template-modal/Preview/template";
import { MessageTemplateButton } from "../../models/blockEdit";
import { AgentOption, AgenticBuilderAgent } from "../agent-block/models";
import {
  AGENT_NODE_SUFFIX,
  ASK_AI_BUTTON_KEY_PREFIX,
  FREE_REPLY_WINDOW_SECONDS,
} from "./constants";
import { AgentAttachment } from "./models";

type OperatorValues = { [buttonKey: string]: { key: string; value: any } };

export class AgentAttachmentHelper {
  // An agent can only be attached to a marketing template.
  static isMarketingTemplate(category?: string): boolean {
    return category?.toUpperCase() === TEMPLATE_CATEGORY.MARKETING;
  }

  // The canvas node an attached agent's node id belongs to (N5-agent -> N5);
  // any other node id is returned as is.
  static hostNodeId(nodeId: string): string {
    return nodeId.endsWith(AGENT_NODE_SUFFIX)
      ? nodeId.slice(0, -AGENT_NODE_SUFFIX.length)
      : nodeId;
  }

  static toAgent(option: AgentOption): AgenticBuilderAgent {
    return {
      source: "my_agents",
      agent_id: option.id,
      agent_name: option.name,
    };
  }

  static isPreSalesAgentButton(
    template: TEMPLATE | undefined,
    buttonText: string
  ): boolean {
    return template?.qrbPayload?.[buttonText]?.isPreSalesAgent === true;
  }

  static preSalesAgentButtonKeys(
    buttonsList: MessageTemplateButton[] = []
  ): string[] {
    return buttonsList
      .map((button) => button.buttonKey)
      .filter((buttonKey) => buttonKey?.startsWith(ASK_AI_BUTTON_KEY_PREFIX));
  }

  static build(
    agent: AgenticBuilderAgent,
    buttonsList: MessageTemplateButton[] = []
  ): AgentAttachment {
    return {
      enabled: true,
      agent,
      role: "pre_sales",
      triggers: {
        // only Pre-sales agent buttons from the builder for now; the backend takes any of the node's buttons
        button_keys: AgentAttachmentHelper.preSalesAgentButtonKeys(buttonsList),
        quote_reply: true,
        free_reply_window_seconds: FREE_REPLY_WINDOW_SECONDS,
      },
    };
  }

  // A button the agent runs on needs no connection, so its branch is cleared.
  // Returns a copy: the saved conf may be frozen store state.
  static releaseAgentButtons(
    operatorValues: OperatorValues = {},
    buttonKeys: string[]
  ): OperatorValues {
    const released = { ...operatorValues };
    buttonKeys.forEach((buttonKey) => {
      if (released[buttonKey]) {
        released[buttonKey] = { ...released[buttonKey], value: "" };
      }
    });
    return released;
  }

  // The canvas draws no handle for a button the agent runs on.
  static hideAgentButtonHandles(
    childNodes: { [buttonKey: string]: any },
    attachment?: AgentAttachment
  ): { [buttonKey: string]: any } {
    const hidden = { ...childNodes };
    (attachment?.triggers.button_keys ?? []).forEach((buttonKey) => {
      if (hidden[buttonKey]) {
        hidden[buttonKey] = {
          key: hidden[buttonKey].key,
          value: null,
          isConnectable: false,
        };
      }
    });
    return hidden;
  }
}
